import { flatten } from 'lodash';
import { useCallback } from 'react';

import {
  ConversationChatMessageActionItemDataFragment,
  ConversationChatMessageAppointmentCancelledItemDataFragment,
  ConversationChatMessageAppointmentItemDataFragment,
  ConversationChatMessageAppointmentQuestionAnswerItemDataFragment,
  ConversationChatMessageAppointmentRescheduledItemDataFragment,
  ConversationChatMessageFileItemDataFragment,
  ConversationChatMessageItemDataFragment,
  ConversationChatMessageMindMessageItemDataFragment,
  ConversationChatMessageQuestionAnswerItemDataFragment,
  ConversationChatMessageStatusChangedItemDataFragment,
  ConversationChatMessageStatusItemDataFragment,
  ConversationChatMessageTextItemDataFragment,
  ConversationChatMessageUrgencyChangedItemDataFragment,
  DialogFlowType,
  MessageActionType,
  MessageType,
  MindFeedbackType,
} from '../../types/api/graphql';
import {
  ChatMessageActionStatus,
  ChatMessageActionType,
  ChatMessageAppointmentStatus,
  ChatMessagePostedByType,
  ChatMessageType,
} from '../enums';
import { ChatMessageItem, ChatMessagePostedBy } from '../types';

const postedByValues: { [key: string]: ChatMessagePostedByType } = {
  System: ChatMessagePostedByType.SYSTEM,
  HealthProfessional: ChatMessagePostedByType.HEALTH_PROFESSIONAL,
  Patient: ChatMessagePostedByType.PATIENT,
  GeneralPractice: ChatMessagePostedByType.GENERAL_PRACTICE,
};

export function useConversationMessageItemFormatter() {
  const onHandleFormat = useCallback(
    (
      data:
        | ConversationChatMessageItemDataFragment
        | ConversationChatMessageTextItemDataFragment
        | ConversationChatMessageAppointmentItemDataFragment
        | ConversationChatMessageAppointmentRescheduledItemDataFragment
        | ConversationChatMessageAppointmentCancelledItemDataFragment
        | ConversationChatMessageAppointmentQuestionAnswerItemDataFragment
        | ConversationChatMessageQuestionAnswerItemDataFragment
        | ConversationChatMessageFileItemDataFragment
        | ConversationChatMessageActionItemDataFragment
        | ConversationChatMessageStatusItemDataFragment
        | ConversationChatMessageMindMessageItemDataFragment
        | ConversationChatMessageStatusChangedItemDataFragment
        | ConversationChatMessageUrgencyChangedItemDataFragment
    ) => {
      let message: ChatMessageItem = {
        id: data.id,
        type: data.type as unknown as ChatMessageType,
        conversationId: data.conversationId,
        showAppointmentConfirmation: false,
        postedBy: {
          ...data.postedBy,
          type: postedByValues[data.postedBy.type],
        } as ChatMessagePostedBy,
        postedAt: data.sendAt,
        removable: data.removable ?? false,
      };

      switch (data.type) {
        case MessageType.DeleteAccountRequest:
        case MessageType.MedicationRequest:
        case MessageType.Thanks:
        case MessageType.Text: {
          const value = data as ConversationChatMessageTextItemDataFragment;

          message = {
            ...message,
            message: value.text ?? '',
          };

          break;
        }
        case MessageType.ConversationStatusChanged: {
          const value =
            data as ConversationChatMessageStatusChangedItemDataFragment;

          message = {
            ...message,

            change: {
              from: value.oldStatus,
              to: value.newStatus,
            },
          };
          break;
        }
        case MessageType.ConversationUrgencyChanged: {
          const value =
            data as ConversationChatMessageUrgencyChangedItemDataFragment;

          message = {
            ...message,
            change: {
              from: value.oldUrgence,
              to: value.newUrgence,
            },
          };
          break;
        }
        case MessageType.Mind: {
          const value =
            data as ConversationChatMessageMindMessageItemDataFragment;
          message = {
            ...message,
            questions: value.questions,
            advice: {
              feedback: value.feedback ?? MindFeedbackType.Unknown,
              readonly: value.isUsed ?? false,
              title: value.mindData?.sessionSummary.adviceTitle ?? undefined,
              sessionSummary: value.mindData?.sessionSummary ?? undefined,
              otherPerson: value.mindData?.otherPerson
                ? {
                    name: value.mindData.otherPerson.name,
                    birthdate: value.mindData.otherPerson.birthdateOnly,
                    gender: value.mindData.otherPerson.genderEnum,
                  }
                : undefined,
            },
          };
          break;
        }
        case MessageType.Status: {
          const value = data as ConversationChatMessageStatusItemDataFragment;
          message = {
            ...message,
            status: value.status,
            message: value.text ?? undefined,
          };
          break;
        }
        case MessageType.Appointment: {
          const value =
            data as ConversationChatMessageAppointmentItemDataFragment;
          message = {
            ...message,
            showAppointmentConfirmation: value?.appointment?.id ?? false,
            appointment: {
              id: value.appointment.id,
              startTime: value.appointment.startTime,
              endTime: value.appointment.endTime,
              type: value.appointment.type,
              healthProfessional: value.appointment.healthProfessional ?? null,
              healthProvider: value.appointment.healthProvider ?? null,
              status: value.appointment
                .actionStatus as unknown as ChatMessageAppointmentStatus,
              message: value.text ?? undefined,
            },
          };

          break;
        }
        case MessageType.AppointmentRescheduled: {
          const value =
            data as ConversationChatMessageAppointmentRescheduledItemDataFragment;

          message = {
            ...message,
            showAppointmentConfirmation: value?.appointment?.id ?? false,
            appointment: {
              id: value.appointment.id,
              startTime: value.appointment.startTime,
              endTime: value.appointment.endTime,
              type: value.appointment.type,
              healthProfessional: value.appointment.healthProfessional ?? null,
              healthProvider: value.appointment.healthProvider ?? null,
              status: value.appointment
                .actionStatus as unknown as ChatMessageAppointmentStatus,
              message: value.text ?? undefined,
            },
          };

          break;
        }
        case MessageType.AppointmentCancelled: {
          const value =
            data as ConversationChatMessageAppointmentCancelledItemDataFragment;

          message = {
            ...message,
            appointment: {
              id: value.appointment.id,
              startTime: value.appointment.startTime,
              endTime: value.appointment.endTime,
              type: value.appointment.type,
              healthProfessional: value.appointment.healthProfessional ?? null,
              healthProvider: value.appointment.healthProvider ?? null,
              status: ChatMessageAppointmentStatus.CANCELED,
              message: value.text ?? undefined,
            },
          };

          break;
        }
        case MessageType.QaWithAppointment: {
          const value =
            data as ConversationChatMessageAppointmentQuestionAnswerItemDataFragment;

          message = {
            ...message,
            questions: value.questions,
          };

          break;
        }
        case MessageType.Qa: {
          const value =
            data as ConversationChatMessageQuestionAnswerItemDataFragment;

          message = {
            ...message,
            questions: value.questions,
          };

          break;
        }
        case MessageType.Action: {
          const value = data as ConversationChatMessageActionItemDataFragment;

          message = {
            ...message,
            message: value.text ?? '',
            showAppointmentConfirmation: value?.appointmentId ?? false,
            actions: flatten(value.groups.map(action => action.actions))
              .map(action => {
                let type = ChatMessageActionType.DEFAULT;

                if (action.dialogFlowType === DialogFlowType.Uwi) {
                  type = ChatMessageActionType.URINE_QUESTIONNAIRE;
                } else if (action.dialogFlowType === DialogFlowType.Anamnesis) {
                  type = ChatMessageActionType.EXPLAIN_COMPLAINT;
                } else if (action.type === MessageActionType.StartAppointment) {
                  type = ChatMessageActionType.APPOINTMENT;
                } else if (action.type === MessageActionType.StartAnamnesis) {
                  type = ChatMessageActionType.EXPLAIN_COMPLAINT;
                }

                return {
                  type,
                  title: action.text,
                  appointment: action.appointment
                    ? {
                        consultTypes: action.appointment.consultTypes ?? [],
                        honorifics:
                          action.appointment?.honorificBlockMinutes?.map(
                            x => x.honorific
                          ) ?? [],
                        healthProfessional:
                          action.appointment.healthProfessional ?? null,
                      }
                    : undefined,
                  status:
                    value.actionStatus as unknown as ChatMessageActionStatus,
                };
              })
              .filter(action => action.type !== ChatMessageActionType.DEFAULT),

            files: value.files.map(file => ({
              file: file.file,
              title: file.originalFileName,
              thumbnail: file.thumbNail,
            })),
          };

          break;
        }
        case MessageType.File: {
          const value = data as ConversationChatMessageFileItemDataFragment;
          message = {
            ...message,
            message: value.text ?? '',
            files: value.files.map(file => ({
              file: file.file,
              title: file.originalFileName,
              thumbnail: file.thumbNail,
            })),
          };

          break;
        }
        default:
          break;
      }

      return message;
    },
    []
  );

  return {
    formatter: onHandleFormat,
  };
}
